import { useCallback, useEffect, useMemo, useState } from 'react';
import { cleanText } from '../utils/cleanupEngine';
import {
  cloneOptions,
  DEFAULT_OPTIONS,
  optionsFromPreset,
  PRESETS,
  PRESET_KEY,
  STORAGE_KEY,
} from '../utils/cleanupOptions';

function loadStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.input === 'string' && parsed.options && typeof parsed.options === 'object') {
      return parsed;
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

function loadPresetId() {
  try {
    const id = localStorage.getItem(PRESET_KEY);
    if (id && typeof id === 'string') return id;
  } catch (_) {
    /* ignore */
  }
  return 'smart';
}

export function useTextCleanerState() {
  const stored = typeof window !== 'undefined' ? loadStored() : null;
  const savedPresetId = loadPresetId();

  const [input, setInput] = useState(stored?.input ?? '');
  const [originalInput, setOriginalInput] = useState(stored?.input ?? '');
  const [options, setOptions] = useState(() => {
    if (stored?.options) return { ...DEFAULT_OPTIONS, ...stored.options };
    if (savedPresetId === 'custom') return cloneOptions(DEFAULT_OPTIONS);
    return optionsFromPreset(Object.prototype.hasOwnProperty.call(PRESETS, savedPresetId) ? savedPresetId : 'smart');
  });
  const [activePresetId, setActivePresetId] = useState(() => {
    if (stored?.options) return 'custom';
    if (savedPresetId === 'custom') return 'custom';
    return Object.prototype.hasOwnProperty.call(PRESETS, savedPresetId) ? savedPresetId : 'smart';
  });

  useEffect(() => {
    const payload = JSON.stringify({ input, options });
    try {
      localStorage.setItem(STORAGE_KEY, payload);
    } catch (_) {
      /* quota */
    }
  }, [input, options]);

  useEffect(() => {
    try {
      localStorage.setItem(PRESET_KEY, activePresetId);
    } catch (_) {
      /* ignore */
    }
  }, [activePresetId]);

  const { output, stats } = useMemo(() => cleanText(input, options), [input, options]);

  const applyPreset = useCallback((presetId) => {
    const next = optionsFromPreset(presetId);
    setOptions(next);
    setActivePresetId(presetId);
  }, []);

  const setOption = useCallback((key, value) => {
    setOptions((prev) => ({ ...prev, [key]: value }));
    setActivePresetId('custom');
  }, []);

  const resetOptionsToDefaults = useCallback(() => {
    applyPreset('smart');
  }, [applyPreset]);

  const clearAll = useCallback(() => {
    setInput('');
    setOriginalInput('');
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {
      /* ignore */
    }
  }, []);

  const clearSavedState = useCallback(() => {
    clearAll();
    applyPreset('smart');
    try {
      localStorage.removeItem(PRESET_KEY);
    } catch (_) {
      /* ignore */
    }
  }, [clearAll, applyPreset]);

  const captureOriginal = useCallback(() => {
    setOriginalInput(input);
  }, [input]);

  const revertInput = useCallback(() => {
    setInput(originalInput);
  }, [originalInput]);

  const swapIo = useCallback(() => {
    setInput(output);
    setOriginalInput(output);
  }, [output]);

  const setInputAndOriginal = useCallback((text) => {
    setInput(text);
    setOriginalInput(text);
  }, []);

  return {
    input,
    setInput,
    output,
    stats,
    options,
    setOption,
    activePresetId,
    applyPreset,
    resetOptionsToDefaults,
    clearAll,
    clearSavedState,
    originalInput,
    captureOriginal,
    revertInput,
    swapIo,
    setInputAndOriginal,
    setOptionsDirect: (patch) => {
      setOptions((prev) => ({ ...prev, ...patch }));
      setActivePresetId('custom');
    },
    exportOptionsSnapshot: () => cloneOptions(options),
  };
}
